import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import type { GeometryResponse, Waypoint } from '@xingroute/schema'
import { useLocale } from '../locale/LocaleContext.js'
import { api } from '../api/client.js'
import { toGpx, trackStats } from '../lib/gpx.js'
import { triggerDownload, toKml } from '../lib/kml.js'
import { IconUpload } from './ui/IconButton.js'

// GpxRail — the GPX + waypoint panel that lives in the detail page. On lg+
// it slots into a clamped `flex-1 min-h-0` parent and scrolls only the waypoint
// list. Below lg it participates in normal document flow so the page has one
// vertical scroll container.

export interface GpxRailProps {
  geometry: GeometryResponse | null
  loading: boolean
  error: string | null
  downloadName: string
  routeId: string
  token?: string | null
  /** Set by RouteMap whenever its edit toggle flips. */
  mapEditing: boolean
  /** Live draft waypoints coming from the map while editing. */
  mapWaypoints: Waypoint[]
  /** Called when the user clicks a waypoint row in the list. The parent
   *  forwards this to RouteMap via its `focusWaypointId` prop. */
  onFocusWaypoint?: (id: string) => void
  activeWaypoint?: { id: string; nonce: number } | null
  /** Show waypoint markers on the map (drives the toggle above the list). */
  showWaypointsOnMap?: boolean
  onToggleShowWaypointsOnMap?: (next: boolean) => void
  edit?: {
    uploading: boolean
    hasGeometry: boolean
    onUpload: (file: File) => void | Promise<void>
    onRemove: () => void | Promise<void>
  }
}

export interface GpxFileActionsProps {
  geometry: GeometryResponse | null
  downloadName: string
  routeId: string
  token?: string | null
  editHref?: string
  edit?: {
    uploading: boolean
    hasGeometry: boolean
    onUpload: (file: File) => void | Promise<void>
  }
  /** When true the GPX/KML download output is the reversed geometry passed
   *  in via `geometry`; this only labels the toggle as active in the UI. */
  reversed?: boolean
  /** Toggle the reversed direction. In view mode this flips a local-only
   *  preview; in editor mode it stages the reverse for the unified Save. When
   *  omitted the reverse button is hidden. */
  onToggleReverse?: () => void
  /** Disable the reverse toggle (e.g. while a save is in flight). */
  reverseDisabled?: boolean
  /** Editor mode: hide ↓GPX, ↓KML and the ✎ edit link (downloads belong on the
   *  read view; the edit link is meaningless inside the editor). */
  editorMode?: boolean
}

export function GpxRail({
  geometry,
  loading,
  error,
  downloadName,
  routeId,
  token,
  mapEditing,
  mapWaypoints,
  onFocusWaypoint,
  activeWaypoint,
  showWaypointsOnMap = false,
  onToggleShowWaypointsOnMap,
  edit,
}: GpxRailProps) {
  const { t } = useLocale()
  const liveWaypoints: Waypoint[] = mapEditing ? mapWaypoints : (geometry?.points ?? [])

  const stats = useMemo(() => {
    if (!geometry || geometry.line.length === 0) return null
    return trackStats(geometry.line.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele })))
  }, [geometry])

  return (
    <section className="flex flex-col gap-3 lg:h-full lg:min-h-0">
      {error && <p className="flex-none text-xs text-[var(--xr-danger)]">{error}</p>}
      {!geometry && !loading && !error && (
        <p className="flex-none text-sm text-[var(--xr-text-muted)]">{t('gpxEmpty')}</p>
      )}
      {loading && (
        <p className="flex-none text-sm text-[var(--xr-text-muted)]">{t('gpxLoading')}</p>
      )}

      {geometry && (
        <>
          <GpxFileActions
            geometry={geometry}
            downloadName={downloadName}
            routeId={routeId}
            token={token}
            edit={edit}
          />

          {stats && (
            <dl className="grid flex-none grid-cols-2 gap-2 sm:grid-cols-4">
              <StatTile label={t('statPoints')} value={stats.pointCount.toLocaleString()} />
              <StatTile label={t('statDistance')} value={`${stats.distanceKm.toFixed(2)} km`} />
              <StatTile label={t('statAscent')} value={`${Math.round(stats.ascentM)} m`} />
              <StatTile label={t('statDescent')} value={`${Math.round(stats.descentM)} m`} />
            </dl>
          )}

          <GpxWaypointList
            editing={mapEditing}
            waypoints={liveWaypoints}
            onFocus={onFocusWaypoint}
            activeWaypoint={activeWaypoint}
            showOnMap={showWaypointsOnMap}
            onToggleShowOnMap={onToggleShowWaypointsOnMap}
          />
        </>
      )}
    </section>
  )
}

export function GpxFileActions({
  geometry,
  downloadName,
  routeId,
  token,
  editHref,
  edit,
  reversed = false,
  onToggleReverse,
  reverseDisabled = false,
  editorMode = false,
}: GpxFileActionsProps) {
  const { t } = useLocale()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const actionClass =
    'inline-flex h-7 min-w-0 items-center justify-center rounded-[var(--xr-radius-full)] border border-[var(--xr-border-default)] bg-transparent px-3 text-xs font-medium text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)] disabled:cursor-not-allowed disabled:opacity-60'
  const reverseActiveClass = reversed
    ? 'bg-[var(--xr-bg-surface-elev)] text-[var(--xr-brand-primary)] border-[var(--xr-brand-primary)]'
    : ''
  // When the user has reversed the preview we have to download the *reversed*
  // GPX, which the server doesn't know about. We synthesise it client-side
  // from the already-reversed `geometry.line`. In the canonical (non-reversed)
  // case we still call the server so per-point time + extensions survive.
  const downloadGpxNow = () => {
    setDownloadError(null)
    if (!geometry) return
    if (reversed) {
      if (geometry.line.length === 0) {
        void downloadServerGpx(routeId, downloadName, token).catch(() => {
          setDownloadError(t('gpxDownloadFailed'))
        })
        return
      }
      triggerDownload(
        `${downloadName}.gpx`,
        'application/gpx+xml',
        toGpx(geometry.line, downloadName, geometry.points),
      )
      return
    }
    void downloadServerGpx(routeId, downloadName, token).catch(() => {
      setDownloadError(t('gpxDownloadFailed'))
    })
  }

  // Editor mode collapses the action surface:
  //   • ⇄ reverse → staged toggle (persisted via the unified Save)
  //   • ⇪ replace GPX (via `edit.onUpload`)
  // ↓GPX / ↓KML / ✎ edit-link are intentionally hidden — downloads belong on
  // the read view, and the edit link makes no sense when already inside the
  // editor.
  const showDownloads = !editorMode
  const showEditLink = !editorMode && editHref
  const showReverse = (geometry?.line.length ?? 0) > 1 && Boolean(onToggleReverse)
  const reverseTitle = reversed ? t('reverseDirectionActive') : t('reverseDirection')

  return (
    <div className="flex flex-none flex-wrap items-center gap-2" data-testid="gpx-file-actions">
      {downloadError ? (
        <p className="basis-full text-xs text-[var(--xr-danger)]" role="alert">
          {downloadError}
        </p>
      ) : null}
      {showReverse && (
        <button
          type="button"
          data-testid="gpx-rail-reverse"
          onClick={onToggleReverse}
          disabled={reverseDisabled}
          aria-pressed={reversed}
          title={reverseTitle}
          aria-label={reverseTitle}
          className={`${actionClass} gap-1 ${reverseActiveClass}`}
        >
          <span aria-hidden>⇄</span>
          <span>{t('editor.gpx.reverseLabel')}</span>
        </button>
      )}
      {showDownloads && (
        <>
          <button
            type="button"
            data-testid="gpx-rail-download-gpx"
            onClick={downloadGpxNow}
            className={`${actionClass} ${onToggleReverse ? 'ml-auto' : ''}`}
          >
            ↓ {t('downloadingGpx')}
          </button>
          <button
            type="button"
            data-testid="gpx-rail-download-kml"
            onClick={() => {
              triggerDownload(
                `${downloadName}.kml`,
                'application/vnd.google-earth.kml+xml',
                toKml(geometry?.line ?? [], downloadName, geometry?.points ?? []),
              )
            }}
            className={actionClass}
          >
            ↓ {t('downloadingKml')}
          </button>
        </>
      )}
      {showEditLink && (
        <Link to={editHref} className={actionClass} data-testid="gpx-edit-link">
          ✎ {t('edit')}
        </Link>
      )}
      {edit && (
        <>
          <input
            ref={fileInputRef}
            id="gpx-rail-upload"
            name="gpx-rail-upload"
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            disabled={edit.uploading}
            className="hidden"
            aria-label={edit.hasGeometry ? t('editor.gpx.replace') : t('uploadGpx')}
            data-testid="gpx-rail-upload"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void edit.onUpload(f)
              e.target.value = ''
            }}
          />
          <button
            type="button"
            className={actionClass}
            disabled={edit.uploading}
            data-testid="gpx-replace"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconUpload />
            <span className="ml-1">{edit.hasGeometry ? t('replaceGpx') : t('uploadGpx')}</span>
          </button>
        </>
      )}
    </div>
  )
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] px-2 py-1.5">
      <dt className="truncate text-[10px] uppercase tracking-wide text-[var(--xr-text-muted)]">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-medium text-[var(--xr-text-primary)]">{value}</dd>
    </div>
  )
}

export function GpxWaypointList({
  editing,
  waypoints,
  onFocus,
  activeWaypoint,
  pinnedIds,
  showOnMap,
  onToggleShowOnMap,
}: {
  editing: boolean
  waypoints: Waypoint[]
  onFocus?: (id: string) => void
  activeWaypoint?: { id: string; nonce: number } | null
  /** When provided, list rows render a pinned-state badge; clicking a row
   *  fires onFocus as a "toggle pin" intent. */
  pinnedIds?: string[]
  showOnMap?: boolean
  onToggleShowOnMap?: (next: boolean) => void
}) {
  const { t, locale } = useLocale()
  const rowRefs = useRef(new Map<string, HTMLButtonElement>())
  const [pulseId, setPulseId] = useState<string | null>(null)
  const pinnedSet = useMemo(() => new Set(pinnedIds ?? []), [pinnedIds])

  useEffect(() => {
    if (!activeWaypoint) return
    const row = rowRefs.current.get(activeWaypoint.id)
    row?.scrollIntoView({ block: 'nearest' })
    setPulseId(activeWaypoint.id)
    const timer = window.setTimeout(() => setPulseId(null), 200)
    return () => window.clearTimeout(timer)
  }, [activeWaypoint?.id, activeWaypoint?.nonce])

  if (waypoints.length === 0) {
    return <p className="flex-none text-xs text-[var(--xr-text-muted)]">{t('waypointsEmpty')}</p>
  }
  // The "show on map" affordance only makes sense in view mode — edit mode
  // already forces markers visible (you can't drag what you can't see).
  const showToggle = onToggleShowOnMap && !editing
  return (
    <div className="flex flex-col gap-2 lg:min-h-0 lg:flex-1 lg:overflow-hidden">
      <div className="flex flex-none items-center justify-between gap-2 text-xs text-[var(--xr-text-muted)]">
        <span>
          {t('statWaypoints')} · {waypoints.length}
        </span>
        {editing ? (
          <span className="text-[var(--xr-accent)]">
            {locale === 'zh' ? '编辑中（未保存）' : 'Editing (unsaved)'}
          </span>
        ) : showToggle ? (
          <label className="inline-flex flex-none cursor-pointer items-center gap-1.5 select-none">
            <input
              id="waypoint-show-on-map"
              name="waypoint-show-on-map"
              type="checkbox"
              checked={Boolean(showOnMap)}
              onChange={(e) => onToggleShowOnMap?.(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-[var(--xr-brand-primary)]"
              data-testid="waypoint-show-on-map"
            />
            <span>{t('waypointsShowAll')}</span>
          </label>
        ) : null}
      </div>
      <ol className="space-y-1.5 pr-1 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
        {waypoints.map((w, i) => {
          const name = locale === 'en' && w.name_en?.trim() ? w.name_en : w.name
          const desc = locale === 'en' && w.desc_en?.trim() ? w.desc_en : w.desc
          const pinned = pinnedSet.has(w.id)
          return (
            <li key={w.id}>
              <button
                type="button"
                ref={(node) => {
                  if (node) rowRefs.current.set(w.id, node)
                  else rowRefs.current.delete(w.id)
                }}
                onClick={() => onFocus?.(w.id)}
                className={
                  'w-full rounded-[var(--xr-radius-sm)] border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--xr-brand-primary)] ' +
                  (pinned
                    ? 'border-[var(--xr-brand-primary)] bg-[var(--xr-brand-primary-soft)] hover:border-[var(--xr-brand-primary)]'
                    : 'border-[var(--xr-border-default)] hover:border-[var(--xr-border-strong)] hover:bg-[var(--xr-bg-surface-elev)] ' +
                      (pulseId === w.id
                        ? 'bg-[var(--xr-brand-primary-soft)]'
                        : 'bg-[var(--xr-bg-surface)]'))
                }
                data-testid="waypoint-row"
                data-pinned={pinned || undefined}
                aria-pressed={pinnedIds ? pinned : undefined}
                title={
                  pinnedIds
                    ? pinned
                      ? locale === 'zh'
                        ? '点击取消固定'
                        : 'Click to unpin from map'
                      : locale === 'zh'
                        ? '点击固定到地图'
                        : 'Click to pin on map'
                    : undefined
                }
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-[var(--xr-text-primary)]">
                    {i + 1}. {name?.trim() || (locale === 'zh' ? '（未命名）' : '(unnamed)')}
                  </span>
                  <span className="flex-none font-mono text-[11px] text-[var(--xr-text-muted)]">
                    {w.lat.toFixed(5)}, {w.lon.toFixed(5)}
                    {w.ele !== undefined ? ` · ${Math.round(w.ele)} m` : ''}
                  </span>
                </div>
                {desc && (
                  <p className="mt-1 whitespace-pre-line text-xs text-[var(--xr-text-secondary)]">
                    {desc}
                  </p>
                )}
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

async function downloadServerGpx(
  routeId: string,
  name: string,
  token?: string | null,
): Promise<void> {
  const text = await api.getRouteGpxText(routeId, token)
  triggerDownload(`${name}.gpx`, 'application/gpx+xml', text)
}
